import { LifeBuoy } from "lucide-react";
import AdminPagination from "../../components/admin/AdminPagination";
import { SUPPORT_STATUS_FILTERS, SUPPORT_CATEGORY_LABELS } from "../../components/admin/adminConstants";

export default function AdminSupportTab({
  supportCases,
  supportStatus,
  supportLoading,
  page,
  totalPages,
  onPageChange,
  selectedSupportCase,
  supportPatchStatus,
  supportPatchNotes,
  supportPatching,
  onStatusFilter,
  onOpenCase,
  onPatchStatusChange,
  onPatchNotesChange,
  onPatchSave,
}) {
  return (
    <div className="mt-4 grid lg:grid-cols-[1fr,380px] gap-4" data-testid="admin-support-cases">
      <div className="zc-card overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-[var(--zc-border)] flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <LifeBuoy size={14} className="text-[var(--zc-accent)]" />
            <span className="text-sm font-semibold">Support cases</span>
          </div>
          <div className="flex flex-wrap gap-1" data-testid="admin-support-status-filters">
            {SUPPORT_STATUS_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => onStatusFilter(f.id)}
                className={
                  "px-3 h-8 text-xs font-semibold rounded-md border transition-colors "
                  + (supportStatus === f.id
                    ? "border-[var(--zc-accent)] text-[var(--zc-accent)] bg-[var(--zc-accent-soft)]"
                    : "border-[var(--zc-border)] text-[var(--zc-text-3)] hover:text-[var(--zc-text)]")
                }
                data-testid={`admin-support-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        {supportLoading && (
          <div className="px-6 py-10 text-center text-[var(--zc-text-3)] text-sm">Loading…</div>
        )}
        {!supportLoading && supportCases.length === 0 && (
          <div className="px-6 py-10 text-center text-[var(--zc-text-3)] text-sm">No support cases match this filter.</div>
        )}
        {supportCases.map((c) => (
          <button
            key={c.case_id}
            type="button"
            onClick={() => onOpenCase(c)}
            className={
              "w-full text-left px-4 sm:px-6 py-4 border-b border-[var(--zc-border)] last:border-0 transition "
              + (selectedSupportCase?.case_id === c.case_id ? "bg-[var(--zc-accent-soft)]" : "hover:bg-[var(--zc-bg-3)]")
            }
            data-testid={`admin-support-row-${c.case_id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{c.subject}</div>
                <div className="text-xs text-[var(--zc-text-3)] mt-1">
                  {c.case_id} · {SUPPORT_CATEGORY_LABELS[c.category] || c.category}
                  {c.booking_id && <> · {c.booking_id}</>}
                </div>
                <div className="text-xs text-[var(--zc-text-2)] mt-1">{c.reporter_name} ({c.reporter_email})</div>
              </div>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-[var(--zc-bg-3)] capitalize shrink-0">
                {(c.status || "open").replace("_", " ")}
              </span>
            </div>
            <p className="text-sm text-[var(--zc-text-2)] mt-2 line-clamp-2">{c.body}</p>
          </button>
        ))}
        <AdminPagination page={page} totalPages={totalPages} onPageChange={onPageChange} testid="admin-support-pagination" />
      </div>

      <div className="zc-card p-5 h-fit lg:sticky lg:top-24" data-testid="admin-support-detail">
        {!selectedSupportCase ? (
          <div className="text-sm text-[var(--zc-text-3)] text-center py-8">Select a case to review and update.</div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="eyebrow mb-1">Case</div>
              <div className="font-semibold">{selectedSupportCase.subject}</div>
              <div className="text-xs text-[var(--zc-text-3)] mt-1 font-mono">{selectedSupportCase.case_id}</div>
            </div>
            <p className="text-sm text-[var(--zc-text-2)] whitespace-pre-wrap">{selectedSupportCase.body}</p>
            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">Status</label>
              <select
                value={supportPatchStatus}
                onChange={(e) => onPatchStatusChange(e.target.value)}
                className="zc-input w-full h-10 px-3 text-sm mt-1"
                data-testid="admin-support-patch-status"
              >
                {SUPPORT_STATUS_FILTERS.filter((f) => f.id !== "all").map((f) => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">Admin notes</label>
              <textarea
                value={supportPatchNotes}
                onChange={(e) => onPatchNotesChange(e.target.value)}
                className="zc-input w-full p-3 text-sm mt-1 h-24"
                placeholder="Internal notes or resolution summary…"
                data-testid="admin-support-patch-notes"
              />
            </div>
            <button
              type="button"
              onClick={onPatchSave}
              disabled={supportPatching}
              className="w-full h-10 zc-btn-primary text-sm disabled:opacity-50"
              data-testid="admin-support-patch-save"
            >
              {supportPatching ? "Saving…" : "Save update"}
            </button>
            {selectedSupportCase.timeline?.length > 0 && (
              <div className="pt-2 border-t border-[var(--zc-border)]">
                <div className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold mb-2">Timeline</div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {[...(selectedSupportCase.timeline || [])].reverse().map((ev, i) => (
                    <div key={`${ev.at}-${i}`} className="text-xs text-[var(--zc-text-2)]">
                      <span className="text-[var(--zc-text-3)]">{ev.at ? new Date(ev.at).toLocaleString() : ""}</span>
                      {" · "}{ev.action}{ev.note ? ` — ${ev.note}` : ""}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
